import { dec, formatMoney, formatQuantity, type Pack, ZERO } from "@bl/domain";
import { Link, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowLeftRight,
  ClipboardCheck,
  Ellipsis,
  FileText,
  FlaskConical,
  Handshake,
  PackageMinus,
  PackageX,
  Pencil,
  Scale,
  TriangleAlert,
  Truck,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { type ItemDetail, type Lot, useItem, useLocations, useMovements, usePacks } from "../api";
import { useMedia } from "../components/DocumentViewer";
import {
  CategoryChip,
  catStyle,
  Dialog,
  Empty,
  ErrorNote,
  Menu,
  MenuDivider,
  MenuHeading,
  MenuItem,
  Section,
  Toolbar,
  ToolbarButton,
  ToolbarSeparator,
  usePageTitle,
} from "../components/ui";
import {
  findSpec,
  formLabel,
  mainSourceOf,
  makerText,
  packPhrase,
  regionText,
  showAvailable,
  showBalance,
  showDate,
  showOnHand,
  showPrice,
  showQuantity,
  showSpec,
  showUnitCost,
  specSourceText,
  USER_SOURCE,
  unitLabel,
} from "../format";
import { HarvestDialog } from "../forms/HarvestDialog";
import {
  EditItemDialog,
  EditLotDialog,
  EditSpecsDialog,
  LinkToCatalogDialog,
} from "../forms/ItemDialogs";
import { MovementDialog, type MovementMode } from "../forms/MovementDialog";
import { useLayout } from "../layout/LayoutProvider";
import { useShortFrom } from "../shortFrom";
import { MovementTable } from "./Movements";
import { beerName } from "./Repitch";

export function ItemPage() {
  const { itemId } = useParams({ from: "/items/$itemId" });
  const { data: item, isLoading, error } = useItem(itemId);
  usePageTitle(item?.displayName ?? "Item");
  const { layout, update } = useLayout();
  const mode = layout.inventory.quantityMode;
  const [dialog, setDialog] = useState<MovementMode | null>(null);
  const [counterparty, setCounterparty] = useState<string | undefined>();
  const [editing, setEditing] = useState(false);
  const [editingLot, setEditingLot] = useState<Lot | null>(null);
  // By id, so the lot shown is always the one the page has just loaded.
  const [viewingLotId, setViewingLotId] = useState<string | null>(null);
  const [editingSpecs, setEditingSpecs] = useState(false);
  const [linking, setLinking] = useState(false);
  const [harvesting, setHarvesting] = useState(false);
  const phone = useMedia("(max-width: 760px)");

  if (isLoading) return <div className="page-body muted">Loading</div>;
  if (error || !item) {
    return (
      <div className="page-body">
        <ErrorNote error={error ?? new Error("Item not found")} />
      </div>
    );
  }

  const open = (m: MovementMode, who?: string) => {
    setCounterparty(who);
    setDialog(m);
  };
  // A menu's choice closes the menu, then does what it says.
  const run = (close: () => void, f: () => void) => () => {
    close();
    f();
  };
  const togglePacks = () =>
    update((l) => ({
      ...l,
      inventory: { ...l.inventory, quantityMode: mode === "pack" ? "unit" : "pack" },
    }));

  return (
    <>
      <Toolbar
        left={
          <>
            <Link to="/" className="toolbar-button" title="Back to inventory">
              <ArrowLeft size={20} />
              <span className="label">Inventory</span>
            </Link>
            <ToolbarSeparator />
            <ToolbarButton
              icon={<Truck size={20} />}
              label="Receive"
              labeled
              onClick={() => open("receive")}
            />
            <ToolbarButton
              icon={<PackageMinus size={20} />}
              label="Use"
              labeled
              onClick={() => open("use")}
            />
            <ToolbarButton
              icon={<ClipboardCheck size={20} />}
              label="Count"
              labeled
              onClick={() => open("adjust")}
            />
            {phone ? null : (
              <>
                <ToolbarButton
                  icon={<ArrowLeftRight size={20} />}
                  label="Move"
                  onClick={() => open("transfer")}
                />
                <ToolbarButton
                  icon={<PackageX size={20} />}
                  label="Dump"
                  title="Throw some away, such as spoiled or spilled stock"
                  onClick={() => open("waste")}
                />
                {item.category === "yeast" ? (
                  <ToolbarButton
                    icon={<FlaskConical size={20} />}
                    label="Harvest"
                    title="Yeast already harvested, such as a brink from before the app"
                    onClick={() => setHarvesting(true)}
                  />
                ) : null}
                <Menu icon={<Handshake size={20} />} label="Borrow" align="left">
                  {(close) => (
                    <>
                      <MenuItem onClick={run(close, () => open("borrow-in"))}>
                        Borrowed from a brewery
                      </MenuItem>
                      <MenuItem onClick={run(close, () => open("borrow-repay"))}>
                        Paid back a loan
                      </MenuItem>
                      <MenuItem onClick={run(close, () => open("lend-out"))}>
                        Lent to a brewery
                      </MenuItem>
                      <MenuItem onClick={run(close, () => open("lend-return"))}>
                        Lent stock came back
                      </MenuItem>
                    </>
                  )}
                </Menu>
              </>
            )}
          </>
        }
        right={
          phone ? (
            // On a phone the rest share one button, so Receive, Use and Count keep their words.
            <Menu icon={<Ellipsis size={20} />} label="More" labeled>
              {(close) => (
                <>
                  <MenuItem onClick={run(close, () => open("transfer"))}>Move</MenuItem>
                  <MenuItem onClick={run(close, () => open("waste"))}>Dump</MenuItem>
                  {item.category === "yeast" ? (
                    <MenuItem onClick={run(close, () => setHarvesting(true))}>Harvest</MenuItem>
                  ) : null}
                  <MenuDivider />
                  <MenuHeading>Borrow</MenuHeading>
                  <MenuItem onClick={run(close, () => open("borrow-in"))}>
                    Borrowed from a brewery
                  </MenuItem>
                  <MenuItem onClick={run(close, () => open("borrow-repay"))}>
                    Paid back a loan
                  </MenuItem>
                  <MenuItem onClick={run(close, () => open("lend-out"))}>
                    Lent to a brewery
                  </MenuItem>
                  <MenuItem onClick={run(close, () => open("lend-return"))}>
                    Lent stock came back
                  </MenuItem>
                  <MenuDivider />
                  <MenuItem checked={mode === "pack"} onClick={togglePacks}>
                    Show amounts in packs
                  </MenuItem>
                  <MenuItem onClick={run(close, () => setEditing(true))}>Edit</MenuItem>
                </>
              )}
            </Menu>
          ) : (
            <>
              <ToolbarButton
                icon={<Scale size={20} />}
                label="Packs"
                aria-pressed={mode === "pack"}
                title="Show amounts in packs (bags, boxes)"
                onClick={togglePacks}
              />
              <ToolbarButton
                icon={<Pencil size={20} />}
                label="Edit"
                onClick={() => setEditing(true)}
              />
            </>
          )
        }
      />
      <div className="page-body">
        <Hero item={item} mode={mode} onLink={() => setLinking(true)} />
        <LocationsSection item={item} mode={mode} />
        <CostSection item={item} mode={mode} />
        <LotsSection
          item={item}
          mode={mode}
          onEdit={setEditingLot}
          onOpen={(lot) => setViewingLotId(lot.id)}
        />
        <SpecsSection
          item={item}
          onEdit={() => setEditingSpecs(true)}
          onLink={() => setLinking(true)}
        />
        <HistorySection item={item} onReceive={() => open("receive")} />
      </div>
      <MovementDialog
        open={dialog !== null}
        mode={dialog ?? "receive"}
        itemId={item.id}
        counterparty={counterparty}
        onClose={() => setDialog(null)}
      />
      <EditItemDialog open={editing} onClose={() => setEditing(false)} item={item} />
      <EditSpecsDialog open={editingSpecs} onClose={() => setEditingSpecs(false)} item={item} />
      <LinkToCatalogDialog open={linking} onClose={() => setLinking(false)} item={item} />
      <HarvestDialog
        open={harvesting}
        onClose={() => setHarvesting(false)}
        itemId={item.id}
        itemName={item.displayName}
      />
      <LotDialog
        item={item}
        lot={item.lots.find((l) => l.id === viewingLotId) ?? null}
        mode={mode}
        onClose={() => setViewingLotId(null)}
        onEdit={(lot) => {
          setViewingLotId(null);
          setEditingLot(lot);
        }}
      />
      <EditLotDialog
        open={editingLot !== null}
        onClose={() => setEditingLot(null)}
        item={item}
        lot={editingLot}
      />
    </>
  );
}

function Hero({
  item,
  mode,
  onLink,
}: {
  item: ItemDetail;
  mode: "unit" | "pack";
  onLink: () => void;
}) {
  const other = mode === "pack" ? "unit" : "pack";
  const { data: packs = [] } = usePacks(item.dimension, item.category);
  // What it comes in is what it has come in: the last delivery's pack first, then any other its
  // lots arrived in. Nobody keeps this list; the deliveries do (Tyler, 2026-09-23).
  const current = item.pack ? packPhrase(item.pack, packs, item.displayUnit, "2") : null;
  const shortFrom = useShortFrom();
  const below = dec(item.available).lt(ZERO);
  const short = below ? shortFrom(item.id) : null;
  const others = [
    ...new Set(
      item.lots
        .map((l) => (l.pack ? packPhrase(l.pack, packs, item.displayUnit, "2") : null))
        .filter((p): p is string => p !== null && p !== current),
    ),
  ];
  return (
    <div className="item-hero" style={catStyle(item.category)}>
      <div>
        <CategoryChip category={item.category} />
        <h1>{item.displayName}</h1>
        <div className="meta">
          {formLabel(item) ? <span>{formLabel(item)}</span> : null}
          {/* A hop's brands and regions are its lots': those on hand (Scott, 2026-10-02). */}
          {makerText(item) ? <span>{makerText(item)}</span> : null}
          {regionText(item) ? <span>{regionText(item)}</span> : null}
          {current ? (
            <span>
              Comes in {current}
              {others.length ? `, and has come in ${others.join(" and ")}` : ""}
            </span>
          ) : null}
          {item.archived ? <span className="chip danger plain">Archived</span> : null}
          {item.catalogProductId ? null : (
            <span>
              Not linked to the catalog.{" "}
              <button type="button" className="link-button" onClick={onLink}>
                Link it
              </button>
            </span>
          )}
        </div>
        {item.description ? (
          <p className="muted" style={{ marginBottom: 0 }}>
            {item.description}
          </p>
        ) : null}
      </div>
      <div className="stock">
        <div className="muted">On hand</div>
        <div className={`big num ${item.onHand.negative ? "negative" : ""}`}>
          {showOnHand(item, mode)}
        </div>
        {item.pack ? <div className="muted num">{showOnHand(item, other)}</div> : null}
        {item.cost.averageOnHand ? (
          <div className="muted num">
            {showUnitCost(item, item.cost.averageOnHand, mode)} average
          </div>
        ) : null}
        {/* What the schedule plans of it, and from when that runs short, as Inventory says it. */}
        {dec(item.planned).gt(ZERO) ? (
          <div className={`num ${below && (short?.soon ?? true) ? "negative" : "muted"}`}>
            {showQuantity(item, item.planned, mode)} planned,{" "}
            {showAvailable(item, item.available, mode, " available")}
            {short ? <span className="short-from">From {showDate(short.on)}</span> : null}
          </div>
        ) : null}
        {item.onHand.negative ? (
          <div className="negative row" style={{ justifyContent: "flex-end", marginTop: 6 }}>
            <TriangleAlert size={15} /> Below zero somewhere. Count it.
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LocationsSection({ item, mode }: { item: ItemDetail; mode: "unit" | "pack" }) {
  const { data: locations = [] } = useLocations();
  const names = new Map(locations.map((l) => [l.id, l.name]));
  const rows = item.onHand.byLocation.filter((b) => !dec(b.quantity).eq(ZERO));
  const max = rows.reduce((m, b) => (dec(b.quantity).gt(m) ? dec(b.quantity) : m), ZERO);
  return (
    <Section
      id="locations"
      title="Where it is"
      aside={rows.length === 1 ? "1 location" : `${rows.length} locations`}
    >
      {rows.length === 0 ? (
        <p className="muted">None on hand.</p>
      ) : (
        <div className="location-bars" style={catStyle(item.category)}>
          {rows.map((b) => {
            const pct =
              max.gt(ZERO) && dec(b.quantity).gt(ZERO)
                ? Number(dec(b.quantity).div(max).times("100").toFixed(1))
                : 0;
            return (
              <div key={b.locationId} className="location-bar">
                <span>{names.get(b.locationId) ?? "Unknown"}</span>
                <span className="track">
                  <span className="fill" style={{ width: `${pct}%`, display: "block" }} />
                </span>
                <span className={`num ${dec(b.quantity).lt(ZERO) ? "negative" : ""}`}>
                  {showBalance(item, b, mode)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

function CostSection({ item, mode }: { item: ItemDetail; mode: "unit" | "pack" }) {
  const c = item.cost;
  const unpriced = !dec(c.unpricedQuantity).eq(ZERO);
  // Harvested yeast has no invoice, only the cost its brews carried on to it (Tyler, 2026-10-07).
  const harvested = item.yeastForm === "slurry";
  const priced = c.last !== null || c.averageOnHand !== null;
  return (
    <Section
      id="cost"
      title="Cost"
      aside={priced ? `${formatMoney(c.valueOnHand)} on hand` : "No prices yet"}
    >
      {!priced ? (
        <p className="muted">
          {harvested
            ? "No cost known. A harvest carries half of what its brew's yeast cost, and this yeast was harvested before the app or from yeast with no price."
            : "No prices yet. Enter the invoice price when receiving, and the average cost of what is on hand shows here."}
        </p>
      ) : (
        <div className="spec-grid">
          <div className="spec">
            <div className="label">Average cost on hand</div>
            <div className="value num">
              {c.averageOnHand
                ? showUnitCost(item, c.averageOnHand, "unit")
                : "Nothing priced on hand"}
            </div>
            <div className="source">
              {c.averageOnHand && item.pack
                ? `${showUnitCost(item, c.averageOnHand, "pack")}. `
                : ""}
              Weighted by what is left in each lot.
            </div>
          </div>
          <div className="spec">
            <div className="label">Value on hand</div>
            <div className="value num">{formatMoney(c.valueOnHand)}</div>
            {unpriced ? (
              <div className="source">
                Leaves out {showQuantity(item, c.unpricedQuantity, mode)} with no price.
              </div>
            ) : null}
          </div>
          {c.last ? (
            <div className="spec">
              <div className="label">Last price paid</div>
              <div className="value num">{showPrice(c.last, c.last.packName)}</div>
              <div className="source">
                {c.last.supplier ? `${c.last.supplier}, ` : ""}
                {c.last.receivedOn
                  ? `received ${showDate(c.last.receivedOn)}`
                  : "received date not known"}
              </div>
            </div>
          ) : harvested ? (
            <div className="spec">
              <div className="label">How it is costed</div>
              <div className="value">Carried on, halved</div>
              <div className="source">
                Each harvest carries half of what its brew's yeast cost; the brew keeps the other
                half.
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Section>
  );
}

function lotAmount(item: ItemDetail, lot: Lot, quantity: string, mode: "unit" | "pack") {
  const pack: Pack | null = lot.pack;
  if (mode === "pack" && pack) return formatQuantity(quantity, { pack });
  return formatQuantity(quantity, { unit: item.displayUnit });
}

function LotsSection({
  item,
  mode,
  onEdit,
  onOpen,
}: {
  item: ItemDetail;
  mode: "unit" | "pack";
  onEdit: (lot: Lot) => void;
  onOpen: (lot: Lot) => void;
}) {
  const { layout } = useLayout();
  const [showEmpty, setShowEmpty] = useState(false);
  const lots = item.lots.filter((l) => showEmpty || !dec(l.quantity).eq(ZERO));
  const hidden = item.lots.length - item.lots.filter((l) => !dec(l.quantity).eq(ZERO)).length;
  const isHop = item.category === "hop";
  // Yeast keeps when it should be pitched by, and harvested yeast its generation and the brew it came
  // out of (Tyler, 2026-10-07).
  const isYeast = item.category === "yeast";
  const harvested = item.yeastForm === "slurry";
  const typicalAlpha = findSpec(item.specs, "alpha_acid");
  return (
    <Section id="lots" title="Lots" aside={`${item.onHand.lots} with stock`}>
      {item.lots.length === 0 ? (
        <p className="muted">No deliveries yet. Receiving creates a lot.</p>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Lot</th>
                  {harvested ? <th>Gen</th> : null}
                  <th>{harvested ? "Harvested" : "Received"}</th>
                  <th>From</th>
                  {isHop ? <th>Brand</th> : null}
                  {isHop ? <th>Region</th> : null}
                  {isHop ? <th>Grower</th> : null}
                  <th className="num">Price</th>
                  {isHop ? <th>Crop</th> : null}
                  {isHop ? <th>Alpha</th> : null}
                  {isYeast ? <th>Pitch by</th> : null}
                  <th className="num">On hand</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lots.map((lot) => {
                  const alpha = findSpec(lot.specs, "alpha_acid");
                  return (
                    <tr key={lot.id}>
                      <td>
                        <LotCode lot={lot} onOpen={() => onOpen(lot)} />
                      </td>
                      {harvested ? (
                        <td>{lot.generation === null ? "" : `G${lot.generation}`}</td>
                      ) : null}
                      <td>{lot.receivedOn ? showDate(lot.receivedOn) : "Not known"}</td>
                      <td className="muted">
                        {lot.harvestedFrom ? (
                          <Link to="/brews/$brewId" params={{ brewId: lot.harvestedFrom.brewId }}>
                            {beerName(lot.harvestedFrom)}
                          </Link>
                        ) : (
                          (lot.supplier?.name ?? lot.counterparty ?? lot.note ?? "")
                        )}
                      </td>
                      {isHop ? <td>{lot.brand ?? ""}</td> : null}
                      {isHop ? <td>{lot.region ?? ""}</td> : null}
                      {isHop ? <td>{lot.grower ?? ""}</td> : null}
                      <td className="num">
                        {lot.price?.carried ? (
                          // No invoice: its share of the yeast its brew pitched, halved each
                          // generation (Tyler, 2026-10-07).
                          <span title="Its share of what the yeast its brew pitched cost: half, carried on from that brew">
                            {showPrice(lot.price, null)} <span className="faint">carried</span>
                          </span>
                        ) : lot.price ? (
                          showPrice(lot.price, lot.pack?.name ?? null)
                        ) : (
                          <span className="faint">none</span>
                        )}
                      </td>
                      {isHop ? <td>{lot.cropYear ?? ""}</td> : null}
                      {isHop ? (
                        <td>
                          {alpha ? (
                            <strong>{showSpec(alpha, layout.preferences.colorUnit).text}</strong>
                          ) : typicalAlpha ? (
                            <span className="faint">
                              {showSpec(typicalAlpha, layout.preferences.colorUnit).text} typical
                            </span>
                          ) : (
                            ""
                          )}
                        </td>
                      ) : null}
                      {isYeast ? <td>{lot.bestBy ? showDate(lot.bestBy) : ""}</td> : null}
                      <td className={`num ${dec(lot.quantity).lt(ZERO) ? "negative" : ""}`}>
                        {lotAmount(item, lot, lot.quantity, mode)}
                      </td>
                      <td className="actions">
                        <button type="button" className="link-button" onClick={() => onEdit(lot)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {hidden > 0 ? (
            <button
              type="button"
              className="link-button"
              style={{ marginTop: 10 }}
              onClick={() => setShowEmpty((s) => !s)}
            >
              {/* A lot with nothing left is depleted (Tyler, 2026-10-03). */}
              {showEmpty
                ? "Hide depleted lots"
                : `Show ${hidden} depleted ${hidden === 1 ? "lot" : "lots"}`}
            </button>
          ) : null}
        </>
      )}
    </Section>
  );
}

/**
 * A lot code, which opens the lot, and beside it the invoice it arrived on.
 *
 * The lot comes first, since a lookup fills it with its lab's figures, and the icon beside it leads to
 * the invoice page, where its document is a click away. Most invoices say nothing about one lot, so the
 * document needs no shortcut of its own (Tyler, 2026-10-15).
 */
export function LotCode({ lot, onOpen }: { lot: Lot; onOpen: () => void }) {
  const invoice = lot.arrivedOn;
  const named = invoice?.invoiceNumber ? `Invoice ${invoice.invoiceNumber}` : "The invoice";
  return (
    <span className="lot-code-cell">
      <button type="button" className="lot-code-link" onClick={onOpen}>
        {lot.lotCode}
        <span className="sr-only">, open this lot</span>
      </button>
      {invoice ? (
        <Link
          to="/invoices/$invoiceId"
          params={{ invoiceId: invoice.id }}
          className="icon-button lot-document"
          title={named}
        >
          <FileText size={13} aria-hidden />
          <span className="sr-only">{named}</span>
        </Link>
      ) : null}
    </span>
  );
}

/**
 * Where a lot's figure came from, with the day it was given unless its name already carries a date
 * ("CMG digital catalog, June 2026"): a lookup's figures say when the supplier answered (invariant 6).
 */
function datedSource(source: { name: string; asOf: string }): string {
  if (source.name === USER_SOURCE || /\b(19|20)\d{2}\b/.test(source.name)) {
    return specSourceText(source);
  }
  return `${source.name}, as of ${showDate(source.asOf)}`;
}

function LotFact({ label, value }: { label: string; value: ReactNode }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/**
 * One lot: what this delivery is, where it is, and what was measured on it (Tyler, 2026-10-15). A figure
 * the lot has of its own, from a certificate or a brewer, shows the item's typical value beside it, so
 * the lab's 14.9% alpha reads against the variety's 14 to 17%.
 */
export function LotDialog({
  item,
  lot,
  mode,
  onClose,
  onEdit,
}: {
  item: ItemDetail;
  lot: Lot | null;
  mode: "unit" | "pack";
  onClose: () => void;
  onEdit: (lot: Lot) => void;
}) {
  return (
    <Dialog open={lot !== null} onClose={onClose} title={`Lot ${lot?.lotCode ?? ""}`}>
      {lot ? (
        <LotView item={item} lot={lot} mode={mode} onClose={onClose} onEdit={() => onEdit(lot)} />
      ) : null}
    </Dialog>
  );
}

function LotView({
  item,
  lot,
  mode,
  onClose,
  onEdit,
}: {
  item: ItemDetail;
  lot: Lot;
  mode: "unit" | "pack";
  onClose: () => void;
  onEdit: () => void;
}) {
  const { layout } = useLayout();
  const colorUnit = layout.preferences.colorUnit;
  const { data: locations = [] } = useLocations();
  const isHop = item.category === "hop";
  const harvested = item.yeastForm === "slurry";
  const invoice = lot.arrivedOn;
  const held = lot.byLocation.filter((b) => !dec(b.quantity).eq(ZERO));
  const placeName = (id: string) => locations.find((l) => l.id === id)?.name ?? "Unknown location";
  const where = held
    .map((b) =>
      held.length > 1
        ? `${placeName(b.locationId)} ${lotAmount(item, lot, b.quantity, mode)}`
        : placeName(b.locationId),
    )
    .join(", ");
  const mainSource = mainSourceOf(lot.specs);
  const mainSpec = lot.specs.find((s) => s.source?.name === mainSource);
  return (
    <>
      <div className="dialog-body">
        <dl className="lot-facts">
          <LotFact
            label={harvested ? "Harvested" : "Received"}
            value={lot.receivedOn ? showDate(lot.receivedOn) : "Not known"}
          />
          <LotFact
            label="From"
            value={
              lot.harvestedFrom ? (
                <Link to="/brews/$brewId" params={{ brewId: lot.harvestedFrom.brewId }}>
                  {beerName(lot.harvestedFrom)}
                </Link>
              ) : (
                (lot.supplier?.name ?? lot.counterparty)
              )
            }
          />
          <LotFact label="Supplier's item no." value={lot.supplierSku} />
          <LotFact label="On hand" value={lotAmount(item, lot, lot.quantity, mode)} />
          <LotFact label="Where" value={where} />
          <LotFact
            label="Price"
            value={
              lot.price
                ? showPrice(lot.price, lot.price.carried ? null : (lot.pack?.name ?? null))
                : null
            }
          />
          {isHop ? <LotFact label="Crop" value={lot.cropYear} /> : null}
          {isHop ? <LotFact label="Brand" value={lot.brand} /> : null}
          {isHop ? <LotFact label="Region" value={lot.region} /> : null}
          {isHop ? <LotFact label="Grower" value={lot.grower} /> : null}
          {harvested ? (
            <LotFact
              label="Generation"
              value={lot.generation === null ? null : `G${lot.generation}`}
            />
          ) : null}
          <LotFact
            label={item.category === "yeast" ? "Pitch by" : "Best by"}
            value={lot.bestBy ? showDate(lot.bestBy) : null}
          />
          <LotFact label="Note" value={lot.note} />
        </dl>
        <div>
          <div className="lot-specs-head">
            <h3>Specs</h3>
            {mainSpec?.source ? <span>Source: {datedSource(mainSpec.source)}</span> : null}
          </div>
          {lot.specs.length === 0 ? (
            <p className="muted">No specs recorded for this lot.</p>
          ) : (
            <div className="spec-grid">
              {lot.specs.map((s) => {
                const shown = showSpec(s, colorUnit);
                const typical = findSpec(item.specs, s.property);
                const ownFigure = typical && typical.source?.name !== s.source?.name;
                const source =
                  s.source && s.source.name !== mainSource ? datedSource(s.source) : null;
                return (
                  <div key={s.property} className="spec" title={s.sourceLabel ?? undefined}>
                    <div className="label">{s.label}</div>
                    <div
                      className="value"
                      title={shown.approximate ? "Converted, so approximate" : undefined}
                    >
                      {shown.text}
                    </div>
                    {ownFigure ? (
                      <div className="source">Typical {showSpec(typical, colorUnit).text}</div>
                    ) : null}
                    {source ? <div className="source">{source}</div> : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="dialog-footer">
        {invoice ? (
          <Link to="/invoices/$invoiceId" params={{ invoiceId: invoice.id }} className="button">
            {invoice.invoiceNumber ? `Invoice ${invoice.invoiceNumber}` : "Open the invoice"}
          </Link>
        ) : null}
        <button type="button" className="button" onClick={onEdit}>
          <Pencil size={16} aria-hidden /> Edit
        </button>
        <button type="button" className="button primary" onClick={onClose}>
          Done
        </button>
      </div>
    </>
  );
}

function SpecsSection({
  item,
  onEdit,
  onLink,
}: {
  item: ItemDetail;
  onEdit: () => void;
  onLink: () => void;
}) {
  const { layout } = useLayout();
  const mainSource = mainSourceOf(item.specs);
  if (item.specs.length === 0) {
    return (
      <Section id="specs" title="Specs">
        <p className="muted">
          No specs recorded for this item.{" "}
          <button type="button" className="link-button" onClick={onEdit}>
            Add some
          </button>
          {item.catalogProductId ? null : (
            <>
              , or{" "}
              <button type="button" className="link-button" onClick={onLink}>
                link it to the catalog
              </button>{" "}
              to bring in the supplier&rsquo;s
            </>
          )}
          .
        </p>
      </Section>
    );
  }
  return (
    <Section id="specs" title="Specs" aside={mainSource ? `Source: ${mainSource}` : undefined}>
      <div className="spec-grid">
        {item.specs.map((s) => {
          const shown = showSpec(s, layout.preferences.colorUnit);
          // Only a spec that came from somewhere else says so (Tyler, 2026-09-20).
          const source = s.source && s.source.name !== mainSource ? specSourceText(s.source) : null;
          return (
            <div key={s.property} className="spec" title={s.sourceLabel ?? undefined}>
              <div className="label">{s.label}</div>
              <div
                className="value"
                title={shown.approximate ? "Converted, so approximate" : undefined}
              >
                {shown.text}
              </div>
              {shown.approximate && s.unit ? (
                <div className="source">Reported as {showSpec(s, s.unit as "lovibond").text}</div>
              ) : null}
              {source ? <div className="source">{source}</div> : null}
            </div>
          );
        })}
      </div>
      <button type="button" className="link-button" style={{ marginTop: 12 }} onClick={onEdit}>
        Edit specs
      </button>
    </Section>
  );
}

function HistorySection({ item, onReceive }: { item: ItemDetail; onReceive: () => void }) {
  const { data, isLoading } = useMovements({ itemId: item.id, limit: 50 });
  const movements = data?.movements ?? [];
  return (
    <Section id="history" title="History" aside={`${unitLabel(item.displayUnit)}, newest first`}>
      {isLoading ? <p className="muted">Loading</p> : null}
      {!isLoading && movements.length === 0 ? (
        <Empty title="Nothing recorded yet">
          <button type="button" className="button primary" onClick={onReceive}>
            <Truck size={16} /> Receive stock
          </button>
        </Empty>
      ) : null}
      {movements.length > 0 ? <MovementTable movements={movements} hideItem /> : null}
    </Section>
  );
}
